import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';

@Injectable()
export class CertificadoService {
    private urlModulo = `${environment.urlAPI}/certificado`;
    
    constructor(private http: HttpClient) { }

    /**
     * Recupera a lista de certificados
     * @returns Promise com a lista de certificados
     */
    public getListaCertificados(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.http.get(`${this.urlModulo}-lista`)
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data.lista);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Recupera os certificados de um fornecedor
     * @param codUsuario Código do usuário
     * @param codSistemaGestao Código do sistema de gestão
     * @returns Promise com os certificados do fornecedor
     */
    public getCertificadosByFornecedor(codUsuario: number, codSistemaGestao: number): Promise<any> {
        let params = new HttpParams()
            .set('codUsuario', codUsuario.toString())
            .set('codSistemaGestao', codSistemaGestao.toString());
        
        return new Promise((resolve, reject) => {
            this.http.get(`${this.urlModulo}-byfornecedor`, { params })
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data.dados);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Recupera o histórico de workflow de recertificação
     * @param codUsuario Código do usuário
     * @param codSistemaGestao Código do sistema de gestão
     * @returns Promise com o histórico de workflow
     */
    public getWorkflowHistoricoRecertificacao(codUsuario: number, codSistemaGestao: number): Promise<any> {
        let params = new HttpParams()
            .set('codUsuario', codUsuario.toString())
            .set('codSistemaGestao', codSistemaGestao.toString());
        
        return new Promise((resolve, reject) => {
            this.http.get(`${this.urlModulo}-workflowhistoricorecertificacao`, { params })
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data.dados);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Recupera o relatório final da auditoria
     * @param codWorkflow Código do workflow
     * @returns Promise com o relatório da auditoria
     */
    public getWorkflowHistoricoRelatorioAuditoria(codWorkflow: number): Promise<any> {
        const params = new HttpParams().set('codWorkflow', codWorkflow.toString());
        
        return new Promise((resolve, reject) => {
            this.http.get(`${this.urlModulo}-workflowhistoricorelatorioauditoria`, { params })
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data.dados);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Recupera o próximo número de certificado
     * @returns Promise com o próximo número de certificado
     */
    public getProximoCertificado(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.http.get(`${this.urlModulo}-proximocertificado`)
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data.numero);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Recupera um certificado pelo seu código
     * @param codCertificado Código do certificado
     * @returns Promise com os dados do certificado
     */
    public getCertificadoByCodigo(codCertificado: number): Promise<any> {
        const params = new HttpParams().set('codCertificado', codCertificado.toString());
        
        return new Promise((resolve, reject) => {
            this.http.get(`${this.urlModulo}-certificadoByCodigo`, { params })
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data.certificado);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Recupera um certificado pelo código do workflow
     * @param codWorkflow Código do workflow
     * @returns Promise com os dados do certificado
     */
    public getCertificadoByCodWorkflow(codWorkflow: number): Promise<any> {
        const params = new HttpParams().set('codWorkflow', codWorkflow.toString());
        
        return new Promise((resolve, reject) => {
            this.http.get(`${this.urlModulo}-certificadoByCodWorkflow`, { params })
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data.certificado);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Recupera dados de certificação externa
     * @param codArquivoCertficacao Código do arquivo de certificação
     * @returns Promise com os dados da certificação externa
     */
    public getDadosCertificaoExterna(codArquivoCertficacao: number): Promise<any> {
        const params = new HttpParams().set('codArquivoCertficacao', codArquivoCertficacao.toString());
        
        return new Promise((resolve, reject) => {
            this.http.get(`${this.urlModulo}-dadosCertificaoExterna`, { params })
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Recupera certificados para análise pelo código do workflow
     * @param codWorkflow Código do workflow
     * @returns Promise com os dados dos certificados para análise
     */
    public getCertificadosAnaliseByCodWorkflow(codWorkflow: number): Promise<any> {
        const params = new HttpParams().set('codWorkflow', codWorkflow.toString());
        
        return new Promise((resolve, reject) => {
            this.http.get(`${this.urlModulo}-dadosCertificadoFornecedorAnalise`, { params })
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Grava um certificado
     * @param payload Dados do certificado
     * @returns Promise com o resultado da operação
     */
    public gravaCertificado(payload: any): Promise<any> {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.urlModulo}-gravacertificado`, payload)
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Grava dados de auditoria de acompanhamento
     * @param payload Dados da auditoria de acompanhamento
     * @returns Promise com o resultado da operação
     */
    public gravaAuditoriaAcompanhamento(payload: any): Promise<any> {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.urlModulo}-gravaauditoriaacompanhamento`, payload)
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Grava um ciclo de auditorias
     * @param payload Dados do ciclo de auditorias
     * @returns Promise com o resultado da operação
     */
    public gravaCicloAuditorias(payload: any): Promise<any> {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.urlModulo}-gravacicloauditorias`, payload)
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Atualiza o grupo de desenvolvimento do certificado
     * @param payload Dados do grupo de desenvolvimento
     * @returns Promise com o resultado da operação
     */
    public atualizaGrupoDesenvolvimentoCertificado(payload: any): Promise<any> {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.urlModulo}-atualizaGrupoDesenvolvimentoCertificado`, payload)
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Atualiza o status de um certificado
     * @param payload Dados do status
     * @returns Promise com o resultado da operação
     */
    public atualizaStatusCertificado(payload: any): Promise<any> {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.urlModulo}-atualizaStatus`, payload)
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Grava um certificado com novo escopo
     * @param payload Dados do certificado com novo escopo
     * @returns Promise com o resultado da operação
     */
    public gravaCertificadoNovoEscopo(payload: any): Promise<any> {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.urlModulo}-gravaCertificadoNovoEscopo`, payload)
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }
}


// WEBPACK FOOTER //
// /app/src/app/services/certificado.service.ts